import React, { useEffect, useState } from 'react';
import { X, Link2, Copy, Check, Plus, RefreshCw } from 'lucide-react';
import api from '../lib/api';

// Standalone payment links (no campaign): choose a terminal, amount, notes —
// generate a /pay/:token link to send a payer.

interface TerminalRow {
  _id: string;
  name: string;
  provider: string;
  isDefault?: boolean;
  isActive?: boolean;
  type?: string;
}

interface LinkRow {
  _id: string;
  title: string;
  amount: number;
  currency: string;
  status: 'open' | 'paid' | 'expired';
  url: string;
  paymentCount?: number;
  createdAt: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const CURRENCIES = ['ILS', 'USD', 'EUR', 'GBP'];
const STATUS_LABEL: Record<string, string> = { open: 'פתוח', paid: 'שולם', expired: 'סגור' };

export default function PaymentLinkDialog({ open, onClose }: Props) {
  const [terminals, setTerminals] = useState<TerminalRow[]>([]);
  const [terminalId, setTerminalId] = useState('');
  const [campaigns, setCampaigns] = useState<{ _id: string; name: string }[]>([]);
  const [campaignId, setCampaignId] = useState('');
  const [contactId, setContactId] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [contactResults, setContactResults] = useState<{ _id: string; name: string; email?: string; phone?: string }[]>([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('ILS');
  const [allowCustomAmount, setAllowCustomAmount] = useState(false);
  const [notes, setNotes] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');

  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const fetchLinks = async () => {
    setLoadingLinks(true);
    try {
      const res = await api.get('/payment-requests', { params: { limit: '50' } });
      setLinks(res.data?.data || []);
    } catch (err) {
      console.error('[PaymentLinkDialog] fetch links failed', err);
    } finally {
      setLoadingLinks(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setTitle(''); setAmount(''); setCurrency('ILS'); setAllowCustomAmount(false);
    setNotes(''); setPayerName(''); setPayerEmail(''); setMessage(null); setCampaignId('');
    setContactId(''); setContactQuery(''); setContactResults([]); setContactOpen(false);

    let cancelled = false;
    (async () => {
      try {
        const [termRes, campRes] = await Promise.all([
          api.get('/terminals'),
          api.get('/campaigns').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        // Tranzila only — the paid-tracking callback hook + paymentRequestId
        // threading is wired for the Tranzila branch of /public/payment-url.
        const list: TerminalRow[] = (termRes.data || []).filter(
          (t: any) => t.isActive !== false && t.type === 'organization' && t.provider === 'tranzila'
        );
        setTerminals(list);
        const def = list.find((t) => t.isDefault) || list[0];
        if (def) setTerminalId(def._id);

        const raw = campRes.data?.data || campRes.data?.campaigns || campRes.data || [];
        setCampaigns(
          (Array.isArray(raw) ? raw : [])
            .map((c: any) => ({ _id: c._id?.toString(), name: c.name || c.title || '' }))
            .filter((c: any) => c._id && c.name)
        );
      } catch (err) {
        console.error('[PaymentLinkDialog] load failed', err);
      }
    })();
    void fetchLinks();
    return () => { cancelled = true; };
  }, [open]);

  // Debounced contact search — runs while the user types in the picker and no
  // contact is selected yet.
  useEffect(() => {
    if (!open || contactId) return;
    const q = contactQuery.trim();
    if (q.length < 2) { setContactResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/contacts', { params: { search: q, limit: '8' } });
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setContactResults(raw.map((c: any) => ({ _id: c._id, name: c.name || 'ללא שם', email: c.email, phone: c.phone })));
        setContactOpen(true);
      } catch { /* ignore */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, contactQuery, contactId]);

  const selectContact = (c: { _id: string; name: string; email?: string; phone?: string }) => {
    setContactId(c._id);
    setContactQuery(c.name);
    setContactOpen(false);
    // Pre-fill payer fields when empty so the payer page shows them.
    setPayerName((prev) => prev || c.name || '');
    setPayerEmail((prev) => prev || c.email || '');
  };

  const clearContact = () => {
    setContactId('');
    setContactQuery('');
    setContactResults([]);
  };

  const handleCreate = async () => {
    if (!terminalId) { setMessage({ type: 'error', text: 'יש לבחור מסוף' }); return; }
    if (!title.trim()) { setMessage({ type: 'error', text: 'יש להזין כותרת' }); return; }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setMessage({ type: 'error', text: 'יש להזין סכום תקין' }); return; }

    setCreating(true);
    setMessage(null);
    try {
      const res = await api.post('/payment-requests', {
        terminalId, title: title.trim(), amount: amt, currency, allowCustomAmount,
        campaignId: campaignId || undefined,
        contactId: contactId || undefined,
        notes: notes.trim() || undefined,
        payerName: payerName.trim() || undefined,
        payerEmail: payerEmail.trim() || undefined,
      });
      setMessage({ type: 'success', text: 'הקישור נוצר! מופיע ברשימה למטה.' });
      setTitle(''); setAmount(''); setNotes(''); setPayerName(''); setPayerEmail('');
      if (res.data?.url) {
        try { await navigator.clipboard.writeText(res.data.url); setCopied(res.data.id); } catch { /* ignore */ }
      }
      void fetchLinks();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.error || 'שגיאה ביצירת הקישור' });
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (row: LinkRow) => {
    try { await navigator.clipboard.writeText(row.url); setCopied(row._id); setTimeout(() => setCopied(null), 2000); }
    catch { /* ignore */ }
  };

  if (!open) return null;

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-[#2C2C2C] rounded-lg text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" dir="rtl" onClick={onClose}>
      <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Link2 className="text-blue-600" size={22} />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">טופס תשלום</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300" aria-label="סגור">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 overflow-auto space-y-4">
          {/* Create form */}
          <div className="space-y-2">
            <input className={inputCls} placeholder="כותרת (מה התשלום עבור)" value={title} onChange={(e) => setTitle(e.target.value)} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">מסוף</label>
                {terminals.length === 0 ? (
                  <div className="text-xs text-red-600 dark:text-red-400 py-2">לא נמצאו מסופים פעילים.</div>
                ) : (
                  <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)} className={inputCls}>
                    {terminals.map((t) => (
                      <option key={t._id} value={t._id}>{t.name}{t.isDefault ? ' (ברירת מחדל)' : ''}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">סכום</label>
                <input className={inputCls} type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">מטבע</label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="relative">
              <label className="text-xs text-gray-500 mb-1 block">שיוך לאיש קשר (אופציונלי)</label>
              <div className="relative">
                <input
                  className={inputCls}
                  placeholder="חיפוש איש קשר לפי שם / מייל / טלפון…"
                  value={contactQuery}
                  onChange={(e) => { setContactQuery(e.target.value); if (contactId) setContactId(''); }}
                  onFocus={() => { if (contactResults.length) setContactOpen(true); }}
                />
                {contactId && (
                  <button type="button" onClick={clearContact} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600" aria-label="נקה">
                    <X size={16} />
                  </button>
                )}
              </div>
              {contactOpen && !contactId && contactResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white dark:bg-[#2C2C2C] border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
                  {contactResults.map((c) => (
                    <button
                      key={c._id}
                      type="button"
                      onClick={() => selectContact(c)}
                      className="w-full text-right px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <div className="font-medium text-gray-900 dark:text-white">{c.name}</div>
                      {(c.email || c.phone) && <div className="text-xs text-gray-500">{[c.email, c.phone].filter(Boolean).join(' · ')}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input className={inputCls} placeholder="שם המשלם (אופציונלי)" value={payerName} onChange={(e) => setPayerName(e.target.value)} />
              <input className={inputCls} placeholder="אימייל המשלם (אופציונלי)" value={payerEmail} onChange={(e) => setPayerEmail(e.target.value)} />
            </div>
            {campaigns.length > 0 && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">שיוך לקמפיין (אופציונלי)</label>
                <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={inputCls}>
                  <option value="">ללא שיוך</option>
                  {campaigns.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <textarea className={inputCls} rows={2} placeholder="הערות (אופציונלי)" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={allowCustomAmount} onChange={(e) => setAllowCustomAmount(e.target.checked)} className="rounded" />
              אפשר למשלם לשנות את הסכום
            </label>

            {message && (
              <div className={`p-2 rounded-lg text-sm ${
                message.type === 'success'
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                  : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              }`}>{message.text}</div>
            )}

            <button
              onClick={handleCreate}
              disabled={creating || !terminalId}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg"
            >
              <Plus size={16} /> {creating ? 'יוצר…' : 'צור קישור תשלום'}
            </button>
          </div>

          {/* Existing links */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase text-gray-500">קישורים קיימים</div>
              <button onClick={fetchLinks} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500" aria-label="רענן">
                <RefreshCw size={14} className={loadingLinks ? 'animate-spin' : ''} />
              </button>
            </div>
            {links.length === 0 ? (
              <div className="text-sm text-gray-500 py-4 text-center">אין קישורים עדיין.</div>
            ) : (
              <div className="space-y-2">
                {links.map((row) => (
                  <div key={row._id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-gray-50 dark:bg-[#222]">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{row.title}</div>
                      <div className="text-xs text-gray-500">
                        {row.currency === 'ILS' ? '₪' : ''}{Number(row.amount).toLocaleString()} {row.currency !== 'ILS' ? row.currency : ''} · {STATUS_LABEL[row.status]}
                        {row.paymentCount ? ` · ${row.paymentCount} תשלומים` : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => copyLink(row)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white dark:bg-[#2C2C2C] border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0"
                    >
                      {copied === row._id ? <><Check size={14} className="text-emerald-600" /> הועתק</> : <><Copy size={14} /> העתק קישור</>}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
